/**
 * Walks every named-item collection in the workbook (one global +
 * one per worksheet) and returns a flat list of NamedRangeInfo for the
 * Info tab section and the searchable dialog.
 *
 * Avoids loading `.value` (which can be a 2D array for range-typed
 * names) — we only need metadata, not the underlying data.
 */
import type { NamedRangeInfo } from "./types";


function mapType(t: Excel.NamedItemType | undefined | null): string {
  switch (t) {
    case Excel.NamedItemType.range:
      return "Range";
    case Excel.NamedItemType.array:
      return "Array";
    case Excel.NamedItemType.string:
      return "String";
    case Excel.NamedItemType.double:
      return "Double";
    case Excel.NamedItemType.integer:
      return "Integer";
    case Excel.NamedItemType.boolean:
      return "Boolean";
    case Excel.NamedItemType.error:
      return "Error";
    default:
      return "Unknown";
  }
}


export async function scanNamedRanges(): Promise<NamedRangeInfo[]> {
  return Excel.run(async (context) => {
    const wbNames = context.workbook.names;
    wbNames.load("items/name,items/type,items/formula,items/comment,items/visible");

    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    // Load sheet-scoped names per worksheet — they live in worksheet.names,
    // separate from the workbook.names collection above.
    interface SheetEntry {
      sheetName: string;
      namesCol: Excel.NamedItemCollection;
    }
    const sheetEntries: SheetEntry[] = sheets.items.map((s) => {
      s.names.load("items/name,items/type,items/formula,items/comment,items/visible");
      return { sheetName: s.name, namesCol: s.names };
    });
    if (sheetEntries.length > 0) await context.sync();

    function toInfo(item: Excel.NamedItem, scope: string): NamedRangeInfo {
      const formula = item.formula ?? "";
      return {
        name: item.name,
        scope,
        type: mapType(item.type),
        formula,
        comment: item.comment ? item.comment : null,
        visible: !!item.visible,
        // A name with a broken target (deleted sheet, removed range, etc.)
        // surfaces as "#REF!" in the formula. Cheap structural check —
        // beats round-tripping NamedItem.isValidReference.
        isValid: !formula.includes("#REF!"),
      };
    }

    const result: NamedRangeInfo[] = [];
    for (const item of wbNames.items) {
      result.push(toInfo(item, "workbook"));
    }
    for (const { sheetName, namesCol } of sheetEntries) {
      for (const item of namesCol.items) {
        result.push(toInfo(item, sheetName));
      }
    }

    // Sort: workbook-scoped first, then alphabetical within each scope.
    result.sort((a, b) => {
      if (a.scope === "workbook" && b.scope !== "workbook") return -1;
      if (a.scope !== "workbook" && b.scope === "workbook") return 1;
      const sc = a.scope.localeCompare(b.scope);
      if (sc !== 0) return sc;
      return a.name.localeCompare(b.name);
    });
    return result;
  });
}


/**
 * Try to parse a refers-to formula into a sheet name + cell address
 * so we can navigate to it. Returns null when the name isn't a range
 * reference (e.g. =37, ="Hello", =SUM(...)).
 *
 * Handles:
 *   =Sheet1!$A$1
 *   ='Sheet With Spaces'!$A$1:$B$10
 *   =Sheet1!A1
 *
 * Does NOT handle multi-sheet or external-workbook references.
 */
export function parseNavigationTarget(
  formula: string,
): { sheetName: string; address: string } | null {
  if (!formula.startsWith("=")) return null;
  const body = formula.slice(1);
  // Quoted sheet name: 'Sheet With Spaces'!A1
  let m = /^'((?:[^']|'')+)'!(.+)$/.exec(body);
  if (m) return { sheetName: m[1].replace(/''/g, "'"), address: m[2] };
  // Unquoted sheet name: Sheet1!A1
  m = /^([^'!]+)!(.+)$/.exec(body);
  if (m) return { sheetName: m[1], address: m[2] };
  return null;
}
